import { motion, useMotionValue, useSpring } from "framer-motion";
import { MapPin, Navigation, CarFront } from "lucide-react";
import { MaskLine } from "./Reveal";
import { mapsUrl, wazeUrl } from "./MatchDetails";
import { heureAff, typeLabel } from "../lib/api";

export const Ticket = ({ match, still = false }) => {
  const rx = useSpring(useMotionValue(0), { stiffness: 120, damping: 14 });
  const ry = useSpring(useMotionValue(0), { stiffness: 120, damping: 14 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 14);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 14);
  };
  const reset = () => { rx.set(0); ry.set(0); };
  return (
    <motion.div
      data-testid="hero-match-ticket"
      onMouseMove={onMove}
      onMouseLeave={reset}
      style={still ? undefined : { rotateX: rx, rotateY: ry, transformPerspective: 900 }}
      initial={still ? false : { opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`relative w-full rounded-[24px] border border-chalk/15 p-4 sm:p-5 ${still ? "bg-ink" : "bg-ink/70 backdrop-blur-xl"}`}
    >
      <div className={`${still ? "mb-4 flex" : "hidden sm:flex"} items-center justify-between font-mono text-xs uppercase tracking-[0.2em] text-dim`}>
        <span className="flex items-center gap-3"><img src="/img/logo-usc.png" alt="Logo USC Jemappes" data-testid="club-logo-ticket" className="h-10 w-auto" />Convocation</span>
        <span className="flex items-center gap-2 text-lime"><span className="pulse-dot" />Officielle</span>
      </div>
      <div className="mb-3 grid grid-cols-[1fr_auto_1fr] items-center gap-3 sm:my-4">
        <p data-testid="hero-club-name" className="font-display !font-semibold text-lg uppercase leading-tight tracking-wide text-chalk sm:text-xl">{match.club}</p>
        <span className="font-mono text-sm text-lime">VS</span>
        <p data-testid="hero-opponent-name" className="text-right font-display !font-semibold text-lg uppercase leading-tight tracking-wide text-chalk sm:text-xl">{match.adversaire}</p>
      </div>
      <div className="ticket-cut my-3" />
      <span data-testid="hero-type-badge" className="mb-3 inline-block rounded-full border border-lime px-3 py-0.5 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-lime">{typeLabel(match.type)}</span>
      <dl className="grid grid-cols-2 gap-3 font-mono text-xs">
        <div><dt className="text-dim uppercase">Date</dt><dd className="mt-1 text-chalk">{match.date}</dd></div>
        <div><dt className="text-dim uppercase">{match.type === "match" || !match.type ? "Coup d'envoi" : "Début"}</dt><dd className="mt-1 text-chalk">{heureAff(match.heure)}</dd></div>
        <div data-testid="hero-rendez-vous" className="col-span-2 flex items-center justify-between gap-3 rounded-2xl bg-lime px-3 py-2 text-ink sm:px-4 sm:py-3">
          <dt className="shrink-0 whitespace-nowrap text-[11px] uppercase leading-tight sm:text-xs">Rendez-vous<span className="block">sur place</span></dt>
          <dd className="text-right font-display text-base uppercase leading-tight sm:text-xl sm:leading-none">{heureAff(match.rendez_vous)}</dd>
        </div>
        {match.type !== "entrainement" && match.afficher_covoit && (match.covoit_lieu || match.covoit_heure) && (
          <div data-testid="hero-covoit" className="col-span-2 flex items-center justify-between gap-3 rounded-xl border border-lime/70 px-3 py-1.5 text-chalk">
            <dt className="flex items-center gap-1.5 text-[11px] uppercase text-lime"><CarFront className="h-3.5 w-3.5 shrink-0" />Covoiturage</dt>
            <dd className="text-right font-mono text-xs font-normal uppercase leading-tight text-chalk">{[match.covoit_heure?.replace(":", "h"), match.covoit_lieu].filter(Boolean).join(" — ")}</dd>
          </div>
        )}
        {(match.adresse || match.lieu) && <div className="col-span-2"><dt className="text-dim uppercase">Adresse</dt><dd data-testid="hero-adresse" className="mt-1 text-chalk">{match.adresse || match.lieu}</dd></div>}
        {match.date_limite && <div data-testid="hero-date-limite" className="col-span-2 rounded-xl border border-signal/70 px-3 py-2 text-chalk"><span className="uppercase text-signal">Réponse obligatoire avant le</span> {match.date_limite}{match.heure_limite ? ` à ${match.heure_limite.replace(":", "h")}` : ""}</div>}
      </dl>
      {mapsUrl(match.adresse || match.lieu) && (
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm text-lime">
          <a href={mapsUrl(match.adresse || match.lieu)} target="_blank" rel="noopener noreferrer" data-testid="hero-maps-link" className="inline-flex items-center gap-2 underline-offset-4 hover:underline">
            <MapPin className="h-4 w-4" /> Google Maps
          </a>
          <a href={wazeUrl(match.adresse || match.lieu)} target="_blank" rel="noopener noreferrer" data-testid="hero-waze-link" className="inline-flex items-center gap-2 underline-offset-4 hover:underline">
            <Navigation className="h-4 w-4" /> Waze
          </a>
        </div>
      )}
    </motion.div>
  );
};

export const Hero = ({ match, children }) => (
  <section data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden">
    <motion.div initial={{ scale: 1.15 }} animate={{ scale: 1.03 }} transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }} className="absolute inset-0">
      <img src="/img/stade.jpg" alt="Stade de football sous les projecteurs" className="h-full w-full object-cover object-[50%_60%]" />
    </motion.div>
    <div className="hero-veil absolute inset-0" />
    <div className="grain absolute inset-0" />

    <div className="relative z-10 mx-auto grid min-h-[100svh] max-w-7xl items-center gap-4 px-4 pb-4 pt-[104px] sm:px-10 sm:pb-6 sm:pt-[136px] lg:grid-cols-2 lg:gap-10">
      <div className="space-y-3 sm:space-y-4">
        <p className="inline-block rounded-full bg-ink/80 px-3 py-1 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-lime sm:text-sm">{match.competition} — {match.domicile ? "À domicile" : "À l'extérieur"}</p>
        <h1 data-testid="hero-title" className="font-display uppercase leading-[0.9] text-chalk text-4xl drop-shadow-[0_2px_12px_rgba(0,0,0,0.8)] sm:text-5xl lg:text-6xl">
          <MaskLine delay={0.15}>Tu es <span className="text-lime">convoqué</span></MaskLine>
          <MaskLine delay={0.3}><span data-testid="hero-title-type">{{ entrainement: "à l'entraînement", tournoi: "au tournoi" }[match.type] ?? "au match"}</span><span className="text-lime">.</span></MaskLine>
        </h1>
        <Ticket match={match} />
      </div>
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}>
        {children}
      </motion.div>
    </div>
  </section>
);
